import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './EnergyForecaster.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function EnergyForecaster() {
  const navigate = useNavigate();
  const [predictionStart, setPredictionStart] = useState('');
  const [hoursAhead, setHoursAhead] = useState(12);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [loadingDataset, setLoadingDataset] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const API_BASE = 'http://localhost:5000/api/energy';

  // Fetch dataset info on mount
  useEffect(() => {
    fetchDatasetInfo();
  }, []);

  const fetchDatasetInfo = async () => {
    setLoadingDataset(true);
    try {
      const response = await fetch(`${API_BASE}/dataset-info`);
      if (response.ok) {
        const info = await response.json();
        setDatasetInfo(info);

        // Set default prediction start to a date in the middle of the dataset
        // This ensures we have enough data before and after
        const midDate = new Date('2008-06-01T00:00:00');
        setPredictionStart(midDate.toISOString().slice(0, 16));
      } else {
        setError('Failed to load dataset information. Please refresh the page.');
      }
    } catch (err) {
      console.error('Failed to fetch dataset info:', err);
      setError('Failed to connect to server. Please ensure the backend is running.');
    } finally {
      setLoadingDataset(false);
    }
  };

  const handlePredict = async () => {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prediction_start: predictionStart,
          hours_ahead: hoursAhead,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Prediction failed');
      }
    } catch (err) {
      setError('Failed to connect to server. The model may be loading...');
    } finally {
      setLoading(false);
    }
  };

  const formatChartData = () => {
    if (!result) return null;

    // Combine historical and prediction timestamps
    const allTimestamps = [
      ...result.historical.timestamps,
      ...result.predictions.timestamps,
    ];

    // Format timestamps for display (show date and hour)
    const labels = allTimestamps.map(ts => {
      const date = new Date(ts);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit'
      });
    });

    // Create datasets
    const historicalData = [
      ...result.historical.values,
      ...new Array(result.predictions.values.length).fill(null),
    ];

    const predictedData = [
      ...new Array(result.historical.values.length).fill(null),
      ...result.predictions.values,
    ];

    const actualData = [
      ...new Array(result.historical.values.length).fill(null),
      ...result.actual.values,
    ];

    return {
      labels,
      datasets: [
        {
          label: 'Historical (Input)',
          data: historicalData,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          tension: 0.1,
          pointRadius: 0,
        },
        {
          label: 'Predicted',
          data: predictedData,
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          borderWidth: 2,
          tension: 0.1,
          pointRadius: 0,
          borderDash: [5, 5],
        },
        {
          label: 'Actual',
          data: actualData,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          tension: 0.1,
          pointRadius: 0,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Energy Consumption Forecast (kW)',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Power (kW)',
        },
      },
      x: {
        title: {
          display: true,
          text: 'Time',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>How This AI Works</h2>

            <div className="explanation-section">
              <h3>Technical Explanation</h3>
              <p>
                <strong>Step 1:</strong> Load the UCI Household Electric Power Consumption dataset containing 2,075,259 hourly measurements from December 2006 to November 2010.
                <strong> Step 2:</strong> Clean the data by interpolating missing values and applying winsorization to remove outliers (clipping at 1st and 99th percentiles).
                <strong> Step 3:</strong> Normalize the data using MinMax scaling to range [0, 1] for optimal LSTM training.
                <strong> Step 4:</strong> Create sequences of 60 consecutive hours as input windows, with each window predicting the next hour.
                <strong> Step 5:</strong> Train a 2-layer LSTM network (128 and 64 units) with dropout regularization, using Adam optimizer and mean squared error loss.
                <strong> Step 6:</strong> Achieve MAE of 0.322 kW and RMSE of 0.461 kW on test data, outperforming GRU and TCN architectures.
                <strong> Step 7:</strong> For multi-step forecasting, use autoregressive prediction where each predicted value becomes input for the next prediction.
                <strong> Step 8:</strong> Deploy via Flask API where users select any 60-hour window from the dataset to generate 1-24 hour forecasts with actual values for comparison.
              </p>
            </div>

            <div className="explanation-section simply-put">
              <h3>Simply Put</h3>
              <p>
                The AI learned patterns in household energy usage by studying 4 years of hourly electricity data. It's like teaching someone to predict tomorrow's weather by showing them the last 60 hours of temperature readings. The model discovered daily patterns (people use more energy in morning and evening), weekly cycles (weekends differ from weekdays), and seasonal trends. When you select a date, it looks at the 60 hours before that moment and predicts what happens next, hour by hour. You can then see how accurate it was by comparing its predictions to what actually happened!
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="energy-page">
        <div className="energy-container">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Portfolio
          </button>

          <header className="energy-header">
            <h1>Energy Consumption Forecaster</h1>
            <p className="subtitle">LSTM-based time series prediction for household energy usage</p>
            <button className="info-btn" onClick={() => setShowModal(true)}>
              ℹ️ How It Works
            </button>
          </header>

          <div className="energy-content">
            <div className="controls-section">
              <h2>Prediction Controls</h2>

              {loadingDataset && (
                <div style={{ padding: '1rem', background: '#eff6ff', borderRadius: '8px', marginBottom: '1rem' }}>
                  <p style={{ margin: 0, color: '#1e40af', fontSize: '0.9rem' }}>
                    Loading dataset information... This may take 30-60 seconds on first load.
                  </p>
                </div>
              )}

              <div className="control-group">
                <label htmlFor="prediction-start">Prediction Start Date & Time</label>
                <input
                  id="prediction-start"
                  type="datetime-local"
                  className="date-input"
                  value={predictionStart}
                  onChange={(e) => setPredictionStart(e.target.value)}
                  min={datasetInfo?.min_prediction_start?.slice(0, 16)}
                  max={datasetInfo?.max_prediction_start?.slice(0, 16)}
                  disabled={loadingDataset}
                />
                <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  {loadingDataset ? (
                    'Loading valid date range...'
                  ) : (
                    <>
                      Model will use 60 hours before this date
                      {datasetInfo && (
                        <>
                          <br />
                          Valid range: Jan 2007 - Nov 2010
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>

              <div className="control-group">
                <label htmlFor="hours-ahead">Hours to Predict</label>
                <div className="hours-selector">
                  <input
                    id="hours-ahead"
                    type="range"
                    className="hours-input"
                    min="1"
                    max="24"
                    value={hoursAhead}
                    onChange={(e) => setHoursAhead(parseInt(e.target.value))}
                    disabled={loadingDataset}
                  />
                  <span className="hours-display">{hoursAhead}h</span>
                </div>
              </div>

              <button
                onClick={handlePredict}
                disabled={loading || !predictionStart || loadingDataset}
                className="predict-button"
              >
                {loadingDataset ? 'Loading Dataset...' : loading ? 'Generating Forecast...' : 'Predict Energy Usage'}
              </button>

              <div className="model-info">
                <h3>Model Details</h3>
                <ul>
                  <li><strong>Architecture:</strong> 2-Layer LSTM</li>
                  <li><strong>Input Window:</strong> 60 hours</li>
                  <li><strong>MAE:</strong> 0.322 kW</li>
                  <li><strong>RMSE:</strong> 0.461 kW</li>
                  <li><strong>Dataset:</strong> UCI Power Consumption</li>
                </ul>
              </div>

              {datasetInfo && (
                <div className="model-info" style={{ marginTop: '1rem' }}>
                  <h3>Available Date Range</h3>
                  <ul>
                    <li><strong>From:</strong> {new Date(datasetInfo.min_prediction_start).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</li>
                    <li><strong>To:</strong> {new Date(datasetInfo.max_prediction_start).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</li>
                    <li><strong>Total Hours:</strong> {datasetInfo.total_hours.toLocaleString()}</li>
                  </ul>
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.5rem', marginBottom: 0 }}>
                    Dataset spans Dec 2006 - Nov 2010
                  </p>
                </div>
              )}
            </div>

            <div className="chart-section">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {loading && (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>Running LSTM model on selected time window...</p>
                </div>
              )}

              {result && !loading && (
                <div className="chart-container">
                  <div style={{ height: '400px' }}>
                    <Line data={formatChartData()} options={chartOptions} />
                  </div>
                  <div className="chart-legend">
                    <div className="legend-item">
                      <div className="legend-color historical"></div>
                      <span>Historical (60hr input window)</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color predicted"></div>
                      <span>Predicted ({hoursAhead}hr forecast)</span>
                    </div>
                    <div className="legend-item">
                      <div className="legend-color actual"></div>
                      <span>Actual (ground truth)</span>
                    </div>
                  </div>
                </div>
              )}

              {!result && !loading && !error && (
                <div className="placeholder">
                  <p>
                    Select a prediction start date and click "Predict Energy Usage" to see the LSTM forecast.
                    <br /><br />
                    The model will use 60 hours of historical data before your selected date to predict the next 1-24 hours.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EnergyForecaster;
